import React, { useState, useRef, useEffect } from 'react';
import Logo from '../../assets/images/Logo.png';

type BulkUploadHelpProps = {
  targetId: string;
};

const BulkUploadHelp = ({ targetId }: BulkUploadHelpProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();

    const popupWidth = 360;
    const popupHeight = 420;

    const gap = 12;
    const padding = 16;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Available spaces around trigger
    const spaceRight = viewportWidth - rect.right;
    const spaceLeft = rect.left;
    const spaceBottom = viewportHeight - rect.bottom;
    const spaceTop = rect.top;

    const style: React.CSSProperties = {
      width: `${popupWidth}px`,
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      overflowY: 'auto',
    };

    // -----------------------------------
    // Horizontal Positioning
    // -----------------------------------

    // Open on right side
    if (spaceRight >= popupWidth) {
      style.left = rect.width + gap;
    }
    // Open on left side
    else if (spaceLeft >= popupWidth) {
      style.right = rect.width + gap;
    }
    // Fallback: center inside viewport
    else {
      style.left = Math.max(padding, viewportWidth / 2 - popupWidth / 2);
    }

    // -----------------------------------
    // Vertical Positioning
    // -----------------------------------

    // Open downward
    if (spaceBottom >= popupHeight) {
      style.top = 0;
    }
    // Open upward
    else if (spaceTop >= popupHeight) {
      style.bottom = 0;
    }
    // Fallback: center vertically
    else {
      style.top = Math.max(padding, viewportHeight / 2 - popupHeight / 2);
    }

    setPopupStyle(style);
  };

  const toggleHelp = () => {
    updatePosition();

    if (isPinned) {
      setIsOpen(false);
      setIsPinned(false);
    } else {
      setIsOpen(true);
      setIsPinned(true);
    }
  };

  const handleMouseEnter = () => {
    updatePosition();

    if (!isPinned) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setIsOpen(false);
    }
  };

  const closePanel = () => {
    setIsOpen(false);
    setIsPinned(false);
  };

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className="position-relative d-inline-block overflow-visible"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="d-inline-flex align-items-center gap-2">
        <span className="fw-500">Bulk Data Transaction</span>

        <button
          type="button"
          id={targetId}
          className="btn btn-sm p-0 border-0 bg-transparent text-primary d-inline-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: 28,
            height: 28,
            lineHeight: 1,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: isPinned ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
          }}
          aria-label="Bulk upload instructions"
          aria-expanded={isOpen}
          onClick={toggleHelp}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={e => {
            if (!isPinned) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            } else {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <i
            className={`ri-question-line fs-18 ${isPinned ? 'fw-bold' : ''}`}
          />
        </button>
      </span>

      {isOpen && (
        <div
          className="position-absolute bg-white rounded shadow-lg p-4"
          style={{
            position: 'absolute',
            ...popupStyle,

            zIndex: 1000,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            animation: 'slideDown 0.2s ease',
            backgroundColor: '#fff',
          }}
        >
          <style>
            {`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-8px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}
          </style>

          <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
            <img
              src={Logo}
              alt="MPM"
              style={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />

            <div>
              <h6 className="mb-0 fw-bold text-dark">Bulk Upload Guidelines</h6>

              <small className="text-muted">Important instructions</small>
            </div>
            {isPinned && (
              <button
                type="button"
                className="btn btn-sm p-0 border-0 bg-transparent text-muted ms-auto"
                onClick={closePanel}
                aria-label="Close"
              >
                <i className="ri-close-line fs-18" />
              </button>
            )}
          </div>

          <div className="mb-0">
            <ul className="ps-3 mb-0" style={{ listStyle: 'none' }}>
              {[
                'Bulk Upload is available only after at least one record has been added using the form. Add a record first to enable Bulk Upload.',
                'Always download the latest records before adding a new one to avoid conflicts or failures.',
                'Do not rename columns or modify sheet structure.',
                'Rows with existing IDs will update records.',
                'Rows without IDs will create new records.',
                'Only .xlsx files are supported.',
              ].map((item, idx) => (
                <li
                  key={idx}
                  className="mb-3 d-flex gap-2"
                  style={{ fontSize: '14px' }}
                >
                  <span
                    className="text-success fw-bold"
                    style={{ flexShrink: 0 }}
                  >
                    •
                  </span>

                  <span className="text-muted">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 pt-3 border-top">
            <small className="text-muted mb-0 d-inline-flex align-items-center flex-wrap gap-x-3 gap-y-2 lh-sm">
              <i
                className={`ri-pushpin-2-line fs-18 flex-shrink-0 px-1 ${isPinned ? 'text-primary' : 'text-warning'}`}
                aria-hidden
              />
              {isPinned ? (
                <span className="d-inline-flex align-items-center flex-wrap gap-x-2">
                  <span>Pinned — click the</span>
                  <i className="ri-question-line fs-18 mx-1 px-1" aria-hidden />
                  <span>or</span>
                  <i className="ri-close-line fs-18 mx-1 px-1" aria-hidden />
                  <span>icons to unpin.</span>
                </span>
              ) : (
                <span className="d-inline-flex align-items-center flex-wrap gap-x-2">
                  <span>Tip: click the</span>
                  <i className="ri-question-line fs-18 mx-1 px-1" aria-hidden />
                  <span>icon to pin.</span>
                </span>
              )}
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadHelp;
